import { useState, useEffect, useCallback } from 'react';
import { getSubdomain, hostSplitCandidates, rememberHostSplit } from '../utils/subdomain';
import { getStatelessPathname } from '../utils/tenant';
import authService from '../services/auth/authService';

/**
 * The split of this host whose client slug is an organization, trying each
 * candidate other than `guessed`. Null when none is.
 */
const findHostSplit = async (guessed) => {
  for (const candidate of hostSplitCandidates()) {
    if (candidate.clientSlug === guessed) continue;
    try {
      const found = await authService.lookupOrganization(candidate.clientSlug);
      if (found?.organization_id) return candidate;
    } catch {
      // Not this one; try the next.
    }
  }
  return null;
};

/**
 * Custom hook for managing organization context
 * Handles subdomain detection and organization resolution
 */
export const useOrganization = () => {
  const [state, setState] = useState({
    organization: null,
    subdomain: null,
    loading: true,
    error: null,
    isResolved: false
  });

  /**
   * Clear organization cache
   * Should be called on logout, login, or when switching organizations
   */
  const clearOrganizationCache = useCallback(() => {
    console.log('[useOrganization] Clearing organization cache');
    localStorage.removeItem('current_organization');
    localStorage.removeItem('org_cache_timestamp');
  }, []);

  /**
   * Check if cached organization is still valid
   * Cache expires after 1 hour or on page refresh for login/onboard pages
   */
  const isCacheValid = useCallback(() => {
    const timestamp = localStorage.getItem('org_cache_timestamp');
    if (!timestamp) return false;

    const cacheAge = Date.now() - parseInt(timestamp);
    const ONE_HOUR = 60 * 60 * 1000;

    // Always invalidate cache on login/onboard pages
    const currentPath = getStatelessPathname();
    const isAuthPage = currentPath.includes('/login') ||
                       currentPath.includes('/onboard') ||
                       currentPath.includes('/auth');

    if (isAuthPage) {
      console.log('[useOrganization] Auth page detected - invalidating cache');
      return false;
    }

    // Check if cache is older than 1 hour
    if (cacheAge > ONE_HOUR) {
      console.log('[useOrganization] Cache expired (> 1 hour old)');
      return false;
    }

    return true;
  }, []);

  /**
   * Fetch organization details from API
   * @param {string} subdomain - The organization subdomain
   */
  const fetchOrganization = useCallback(async (subdomain) => {
    try {
      setState(prev => ({ ...prev, loading: true, error: null }));

      // Always try to fetch from API first
      console.log('[useOrganization] Fetching organization from API:', subdomain);
      let response = await authService.lookupOrganization(subdomain);

      // A "<branch>-<client>" host with no recorded split was split at its
      // first hyphen, which is wrong for a hyphenated branch: "austin-branch-
      // loaninghub" guessed org "branch-loaninghub". Try the other splits, as
      // the backend does, and record the one that is an organization.
      if (!response?.organization_id) {
        const split = await findHostSplit(subdomain);
        if (split) {
          rememberHostSplit(split);
          subdomain = split.clientSlug;
          // Again, now that every request carries the real branch
          // (X-Branch-Slug), so the branch's own Stytch organization comes back.
          response = await authService.lookupOrganization(subdomain);
        }
      }

      if (response && response.organization_id) {
        // API returns the organization directly, not wrapped
        // Make sure we have the correct name from API
        const orgData = {
          ...response,
          name: response.name || response.organization_name || 'Organization'  // Handle different field names
        };

        setState({
          organization: orgData,
          subdomain: subdomain,
          loading: false,
          error: null,
          isResolved: true
        });

        // Store in localStorage with timestamp for cache management
        localStorage.setItem('current_organization', JSON.stringify(orgData));
        localStorage.setItem('org_cache_timestamp', Date.now().toString());
        console.log('[useOrganization] Organization resolved from API:', orgData.name);
      } else if (response?.success === false) {
        // Handle 404 or error response
        setState({
          organization: null,
          subdomain: subdomain,
          loading: false,
          error: response.error || 'Organization not found',
          isResolved: true
        });
      } else {
        setState({
          organization: null,
          subdomain: subdomain,
          loading: false,
          error: 'Organization not found',
          isResolved: true
        });
      }
    } catch (error) {
      console.error('[useOrganization] Error fetching organization:', error);
      setState({
        organization: null,
        subdomain: subdomain,
        loading: false,
        error: error.message || 'Failed to fetch organization',
        isResolved: true
      });
    }
  }, []);

  /**
   * Detect and resolve organization on mount
   */
  useEffect(() => {
    const detectOrganization = async () => {
      // Detect current subdomain first
      const detectedSubdomain = getSubdomain();

      // Check if we have a valid cached organization
      const cachedOrg = localStorage.getItem('current_organization');

      if (cachedOrg && detectedSubdomain && isCacheValid()) {
        try {
          const parsed = JSON.parse(cachedOrg);

          // Validate cached organization matches current subdomain
          const cachedSlug = parsed.slug || parsed.subdomain;
          if (cachedSlug === detectedSubdomain) {
            // Ensure the name field is present
            const orgData = {
              ...parsed,
              name: parsed.name || parsed.organization_name || 'Organization'
            };
            console.log('[useOrganization] Using cached organization (valid cache):', orgData.name);
            setState({
              organization: orgData,
              subdomain: detectedSubdomain,
              loading: false,
              error: null,
              isResolved: true
            });
            return;
          } else {
            // Cached organization doesn't match current subdomain - clear cache and fetch fresh
            console.log('[useOrganization] Cached org slug mismatch:', cachedSlug, 'vs current:', detectedSubdomain);
            clearOrganizationCache();
          }
        } catch (e) {
          console.error('[useOrganization] Failed to parse cached organization:', e);
          clearOrganizationCache();
        }
      } else if (cachedOrg && !isCacheValid()) {
        // Cache exists but is invalid - clear it
        console.log('[useOrganization] Cache invalid - clearing');
        clearOrganizationCache();
      }

      if (detectedSubdomain) {
        // Always fetch fresh data when cache is invalid or missing
        console.log('[useOrganization] Fetching fresh organization data for subdomain:', detectedSubdomain);
        await fetchOrganization(detectedSubdomain);
      } else {
        // No subdomain detected
        setState({
          organization: null,
          subdomain: null,
          loading: false,
          error: null,
          isResolved: true
        });
      }
    };

    detectOrganization();
  }, [fetchOrganization, clearOrganizationCache, isCacheValid]);

  /**
   * Manually set organization (for development/testing)
   */
  const setOrganization = useCallback((subdomain) => {
    console.log('[useOrganization] Manually setting organization for subdomain:', subdomain);
    fetchOrganization(subdomain);
  }, [fetchOrganization]);

  /**
   * Force refresh organization from API
   */
  const refreshOrganization = useCallback(async () => {
    const currentSubdomain = state.subdomain || getSubdomain();
    if (currentSubdomain) {
      clearOrganizationCache();
      await fetchOrganization(currentSubdomain);
    }
  }, [state.subdomain, fetchOrganization, clearOrganizationCache]);

  return {
    organization: state.organization,
    subdomain: state.subdomain,
    loading: state.loading,
    error: state.error,
    isResolved: state.isResolved,
    setOrganization,
    refreshOrganization,
    clearOrganizationCache
  };
};

export default useOrganization;
